import axiosInstance from '../utils/axios.instance';
import { API_ENDPOINTS } from '../config/api.config';

/**
 * Service de gestion des sous-dossiers (folders) au sein des commissions
 * Utilise les API backend pour la persistance partagée
 */
export const folderService = {
  /**
   * Récupérer tous les dossiers d'une commission
   * @param {number} commissionId
   * @returns {Promise<Array>}
   */
  getFoldersByCommission: async (commissionId) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.FOLDERS.BY_COMMISSION(commissionId));
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des dossiers:', error);
      throw error;
    }
  },

  /**
   * Créer un nouveau dossier
   * @param {number} commissionId
   * @param {Object} folderData - { name, description }
   * @returns {Promise<Object>}
   */
  createFolder: async (commissionId, folderData) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.FOLDERS.CREATE(commissionId),
        folderData
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création du dossier:', error);
      throw error;
    }
  },

  /**
   * Mettre à jour un dossier (renommer, changer description)
   * @param {number} commissionId
   * @param {number} folderId
   * @param {Object} folderData - { name, description }
   * @returns {Promise<Object>}
   */
  updateFolder: async (commissionId, folderId, folderData) => {
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.FOLDERS.UPDATE(commissionId, folderId),
        folderData
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du dossier:', error);
      throw error;
    }
  },

  /**
   * Renommer un dossier (raccourci pour updateFolder)
   * @param {number} commissionId
   * @param {number} folderId
   * @param {string} newName
   * @returns {Promise<Object>}
   */
  renameFolder: async (commissionId, folderId, newName) => {
    return folderService.updateFolder(commissionId, folderId, { name: newName });
  },

  /**
   * Supprimer un dossier
   * @param {number} commissionId
   * @param {number} folderId
   * @returns {Promise<void>}
   */
  deleteFolder: async (commissionId, folderId) => {
    try {
      await axiosInstance.delete(API_ENDPOINTS.FOLDERS.DELETE(commissionId, folderId));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du dossier:', error);
      throw error;
    }
  },

  /**
   * Récupérer un dossier par son ID
   * @param {number} commissionId
   * @param {number} folderId
   * @returns {Promise<Object|null>}
   */
  getFolderById: async (commissionId, folderId) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.FOLDERS.BY_ID(commissionId, folderId));
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération du dossier:', error);
      return null;
    }
  },

  /**
   * Récupérer les archives d'un dossier
   * @param {number} commissionId
   * @param {number} folderId
   * @returns {Promise<Array>}
   */
  getFolderArchives: async (commissionId, folderId) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.FOLDERS.ARCHIVES(commissionId, folderId));
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des archives du dossier:', error);
      return [];
    }
  },
};

/**
 * Service pour gérer l'assignation des archives aux dossiers
 */
export const archiveFolderService = {
  /**
   * Assigner une archive à un dossier
   * @param {number} commissionId
   * @param {number} folderId
   * @param {number} archiveId
   * @returns {Promise<void>}
   */
  assignArchiveToFolder: async (commissionId, folderId, archiveId) => {
    try {
      await axiosInstance.post(
        API_ENDPOINTS.FOLDERS.ASSIGN_ARCHIVE(commissionId, folderId),
        { archiveId }
      );
    } catch (error) {
      console.error('Erreur lors de l\'assignation de l\'archive au dossier:', error);
      throw error;
    }
  },

  /**
   * Retirer une archive d'un dossier
   * @param {number} commissionId
   * @param {number} folderId
   * @param {number} archiveId
   * @returns {Promise<void>}
   */
  removeArchiveFromFolder: async (commissionId, folderId, archiveId) => {
    try {
      await axiosInstance.delete(
        API_ENDPOINTS.FOLDERS.REMOVE_ARCHIVE(commissionId, folderId, archiveId)
      );
    } catch (error) {
      console.error('Erreur lors du retrait de l\'archive du dossier:', error);
      throw error;
    }
  },

  /**
   * Récupérer le dossier d'une archive (depuis la liste des dossiers)
   * @param {number} archiveId
   * @param {Array} folders - Liste des dossiers avec leurs archives
   * @returns {Object|null}
   */
  getArchiveFolder: (archiveId, folders) => {
    try {
      for (const folder of folders) {
        if (folder.archives && folder.archives.find(a => a.id === archiveId)) {
          return folder;
        }
      }
      return null;
    } catch (error) {
      console.error('Erreur lors de la récupération du dossier de l\'archive:', error);
      return null;
    }
  },

  /**
   * Récupérer toutes les archives d'un dossier
   * @param {number} folderId
   * @param {Array} folders - Liste des dossiers avec leurs archives
   * @returns {Array}
   */
  getArchivesInFolder: (folderId, folders) => {
    try {
      const folder = folders.find(f => f.id === folderId);
      return folder?.archives || [];
    } catch (error) {
      console.error('Erreur lors de la récupération des archives du dossier:', error);
      return [];
    }
  },

  /**
   * Récupérer les archives sans dossier d'une commission
   * @param {Array} allArchives - Toutes les archives de la commission
   * @param {Array} folders - Tous les dossiers avec leurs archives
   * @returns {Array}
   */
  getArchivesWithoutFolder: (allArchives, folders) => {
    try {
      const archivesInFolders = new Set();
      folders.forEach(folder => {
        if (folder.archives) {
          folder.archives.forEach(archive => archivesInFolders.add(archive.id));
        }
      });

      return allArchives.filter(archive => !archivesInFolders.has(archive.id));
    } catch (error) {
      console.error('Erreur lors de la récupération des archives sans dossier:', error);
      return allArchives;
    }
  },
};

export default folderService;
